import { useCallback, useMemo, type ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  FormChatNumberPicker,
  MonthlyFeeFormChatSummaryCard,
  type FormChatEditableFieldOption,
  type FormChatFieldInput,
  type FormChatListOption,
  type MonthlyFeeChatSummary,
} from "gsintegrations";
import { trackFinancialEvent } from "../analytics/financial";
import type {
  FormChatMessageRequest,
  FormChatMessageResponse,
  FormChatState,
  MonthlyFeeReferenceData,
} from "../repositories/formChat.repository";
import { formChatRepository } from "../repositories/formChat.repository";
import { terreiroRepository } from "../repositories/terreiroRepository";
import { extractAxiosApiError } from "../utils/extractAxiosApiError";
import { useCachedTerreiro } from "./useCachedTerreiro";
import { financialQueryKeys } from "./useFinancialQueries";
import { useFormChatSubmitMonthlyFee } from "./useFormChatSubmitMonthlyFee";
import { useProductAnalyticsBase } from "./useProductAnalyticsContext";

const PAYMENT_METHOD_REPLIES = [
  "Dinheiro",
  "Pix",
  "Cheque",
  "Transferência",
  "Cartão crédito",
  "Cartão débito",
  "Boleto",
];

const MIN_DUE_DAY = 1;
const MAX_DUE_DAY = 31;

const EDITABLE_FIELDS: Array<{ key: string; label: string }> = [
  { key: "monthlyFee", label: "Valor" },
  { key: "dueDay", label: "Dia de vencimento" },
  { key: "paymentMethod", label: "Forma de pagamento" },
  { key: "receivingAccountId", label: "Conta de recebimento" },
];

const LOCAL_FIELD_INPUTS: Record<string, Omit<FormChatFieldInput, "field">> = {
  monthlyFee: { type: "number", numberFormat: "currency", allowFreeText: true },
  dueDay: { type: "number", numberFormat: "integer", allowFreeText: true },
  paymentMethod: { type: "quick_replies", quickReplies: PAYMENT_METHOD_REPLIES },
};

const NUMERIC_FIELDS = new Set(["monthlyFee", "dueDay"]);

type TerreiroBankAccount = {
  id: string;
  name?: string;
  agency?: string | null;
  account?: string | null;
  bankAccountOption?: { name?: string; image?: string | null } | null;
};

/** Same label the classic MonthlyFeeForm select shows. */
export const formatMonthlyFeeAccountLabel = (account: TerreiroBankAccount) => {
  const bankName = account.bankAccountOption?.name || account.name || "Conta";
  return account.agency && account.account
    ? `${bankName} - Ag ${account.agency} - Conta ${account.account}`
    : bankName;
};

const formatAmount = (amount: unknown) =>
  typeof amount === "number"
    ? amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : undefined;

interface UseMonthlyFeeFormChatOptions {
  enabled: boolean;
  monthlyFeeId?: string;
  confirmLabel: string;
  sessionId?: string;
  formState: FormChatState;
  summary?: MonthlyFeeChatSummary;
  onResponse: (response: FormChatMessageResponse) => void;
  onError?: (error: { code: string; message: string }) => void;
  onReset: () => void;
  onEdit?: () => void;
}

export function useMonthlyFeeFormChatHelpers({
  enabled,
  monthlyFeeId,
  confirmLabel,
  sessionId,
  formState,
  summary,
  onResponse,
  onError,
  onReset,
  onEdit,
}: UseMonthlyFeeFormChatOptions) {
  const productBase = useProductAnalyticsBase();
  const { id: terreiroId } = useCachedTerreiro() ?? {};
  const queryTerreiroId = enabled ? terreiroId : undefined;
  const { data: bankAccounts = [] } = useQuery<TerreiroBankAccount[]>({
    queryKey: financialQueryKeys.bankAccounts(queryTerreiroId),
    queryFn: () => terreiroRepository.getTerreiroBankAccounts(queryTerreiroId as string),
    enabled: Boolean(queryTerreiroId),
    staleTime: 1000 * 60 * 5,
  });
  const { submitMonthlyFeeByChat, navigateBack, isSubmitting } =
    useFormChatSubmitMonthlyFee({ monthlyFeeId });

  const accountOptions = useMemo<FormChatListOption[]>(
    () =>
      bankAccounts.map((account) => ({
        id: account.id,
        label: formatMonthlyFeeAccountLabel(account),
        imageUrl: account.bankAccountOption?.image ?? null,
      })),
    [bankAccounts],
  );

  const monthlyFeeReferenceData = useMemo<MonthlyFeeReferenceData>(
    () => ({
      bankAccounts: accountOptions.map((option) => ({
        id: option.id,
        name: option.label,
      })),
    }),
    [accountOptions],
  );

  const { mutateAsync: sendMonthlyFeeMutation, isPending } = useMutation({
    mutationKey: ["form-chat", "monthly-fee-message"],
    mutationFn: async (
      payload: FormChatMessageRequest,
    ): Promise<FormChatMessageResponse> => {
      if (!terreiroId) throw new Error("Terreiro scope required");
      return formChatRepository.sendMessage(terreiroId, payload);
    },
    onError: () => {
      trackFinancialEvent("financial_form_chat_submit_error", productBase, {
        transaction_type: "monthly_fee",
      });
    },
  });

  const sendMonthlyFeeRequest = useCallback(
    async (
      payload: Omit<FormChatMessageRequest, "entityType">,
    ): Promise<FormChatMessageResponse | undefined> => {
      if (!terreiroId || !monthlyFeeId) return undefined;
      try {
        const response = await sendMonthlyFeeMutation({
          entityType: "monthly_fee",
          referenceData: monthlyFeeReferenceData,
          mode: "edit",
          targetEntityId: monthlyFeeId,
          ...payload,
        });
        onResponse(response);
        return response;
      } catch (err: unknown) {
        onError?.(extractAxiosApiError(err));
        throw err;
      }
    },
    [
      monthlyFeeId,
      monthlyFeeReferenceData,
      onError,
      onResponse,
      sendMonthlyFeeMutation,
      terreiroId,
    ],
  );

  const getFieldOptions = useCallback(
    (field: string | undefined): FormChatListOption[] =>
      field === "receivingAccountId" ? accountOptions : [],
    [accountOptions],
  );

  const isListField = useCallback(
    (field: string | undefined) => field === "receivingAccountId",
    [],
  );

  const getFieldInput = useCallback(
    (field: string | undefined): FormChatFieldInput | undefined => {
      if (!field) return undefined;
      if (field === "receivingAccountId") {
        return {
          type: "list_select",
          field,
          searchable: true,
          openListLabel: "Ver contas",
          changeLabel: "Alterar",
          allowFreeText: false,
          topSuggestions: accountOptions.slice(0, 4).map((option) => option.label),
        };
      }
      const local = LOCAL_FIELD_INPUTS[field];
      return local
        ? { ...local, field }
        : { type: "text", field, allowFreeText: true };
    },
    [accountOptions],
  );

  /** Numeric fields always use the local input so the chat opens the numeric keyboard. */
  const resolveFieldInput = useCallback(
    (
      field: string | undefined,
      serverFieldInput: FormChatFieldInput | undefined,
    ): FormChatFieldInput | undefined =>
      field && NUMERIC_FIELDS.has(field)
        ? getFieldInput(field)
        : (serverFieldInput ?? getFieldInput(field)),
    [getFieldInput],
  );

  const getFieldPanel = useCallback(
    (params: {
      field: string | undefined;
      isLoading: boolean;
      onValue: (value: number) => void;
    }): ReactNode => {
      if (params.field !== "dueDay") return undefined;
      const current = Number(formState.dueDay);
      return (
        <FormChatNumberPicker
          key="monthly-fee-dueDay"
          title="Dia de vencimento"
          label="Dia do mês"
          value={
            Number.isInteger(current) && current >= MIN_DUE_DAY && current <= MAX_DUE_DAY
              ? current
              : 10
          }
          min={MIN_DUE_DAY}
          max={MAX_DUE_DAY}
          helperText="A mensalidade vence todo mês neste dia (1 a 31)."
          isLoading={params.isLoading}
          onConfirm={params.onValue}
        />
      );
    },
    [formState.dueDay],
  );

  const editableFieldOptions = useMemo<FormChatEditableFieldOption[]>(() => {
    const currentValues: Record<string, string | undefined> = {
      monthlyFee: summary?.monthlyFeeLabel ?? formatAmount(formState.monthlyFee),
      dueDay:
        (summary?.dueDay ?? formState.dueDay)
          ? `Dia ${summary?.dueDay ?? formState.dueDay}`
          : undefined,
      paymentMethod: summary?.paymentMethod ?? formState.paymentMethod,
      receivingAccountId:
        summary?.receivingAccountName ?? formState.receivingAccountName,
    };
    return EDITABLE_FIELDS.map((field) => ({
      key: field.key,
      label: field.label,
      currentValue: currentValues[field.key],
    }));
  }, [formState, summary]);

  const handleConfirmMonthlyFee = useCallback(async () => {
    if (!summary || !sessionId) {
      onError?.({
        code: "FORM_CHAT_INCOMPLETE",
        message: "Revise os dados da mensalidade no chat antes de confirmar.",
      });
      return;
    }
    let response: FormChatMessageResponse | undefined;
    try {
      response = await sendMonthlyFeeRequest({
        sessionId,
        message: "",
        action: "confirm",
      });
    } catch {
      return;
    }
    if (!response || response.currentStep !== "complete") return;

    try {
      await submitMonthlyFeeByChat({ ...formState, ...response.formState });
      onReset();
    } catch (err: unknown) {
      onError?.(
        extractAxiosApiError(
          err,
          "Não foi possível salvar a mensalidade. Revise os dados e tente novamente.",
        ),
      );
    }
  }, [
    formState,
    onError,
    onReset,
    sendMonthlyFeeRequest,
    sessionId,
    submitMonthlyFeeByChat,
    summary,
  ]);

  const transcribeAudio = useCallback(
    async (audio: Blob): Promise<string> => {
      if (!terreiroId) throw new Error("Terreiro scope required");
      const response = await formChatRepository.transcribeAudio(terreiroId, audio, {
        entityType: "monthly_fee",
        mode: "edit",
      });
      return response.transcript;
    },
    [terreiroId],
  );

  const isBusy = isPending || isSubmitting;

  const summaryCard = useMemo(() => {
    if (!summary) return null;
    return (
      <MonthlyFeeFormChatSummaryCard
        summary={summary}
        confirmLabel={confirmLabel}
        isLoading={isBusy}
        onConfirm={handleConfirmMonthlyFee}
        onEdit={onEdit}
      />
    );
  }, [confirmLabel, handleConfirmMonthlyFee, isBusy, onEdit, summary]);

  return {
    monthlyFeeReferenceData,
    sendMonthlyFeeRequest,
    handleConfirmMonthlyFee,
    summaryCard,
    editableFields: EDITABLE_FIELDS,
    editableFieldOptions,
    getFieldOptions,
    getFieldInput,
    resolveFieldInput,
    getFieldPanel,
    isListField,
    transcribeAudio,
    navigateBack,
    isPending: isBusy,
  };
}
